import queryString from 'query-string'
import H from 'history'
import { toFeatureState } from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { TheNightlySection } from '@news-mono/common'
import { IconChevronUpTN, metrics } from '@news-mono/component-library'
import {
    AdTargeting,
    ContentDataTypes,
    getPaginatedCanonicalUrl,
    GetRouteAdTargeting,
    PageType,
    parsePageParam,
    TogglesReduxState,
    Topic,
} from '@news-mono/web-common'
import { ClassificationV4DTO } from '@west-australian-newspapers/publication-types'
import React from 'react'
import { layout } from '../../../App.routing'
import { sectionNavItems } from '../../../navigation'
import { wrappingContexts } from '../../context-wrapper'
import { getParentTopicComposition } from './get-parent-topic-route'
import { getSubTopicComposition } from './get-sub-topic-route'

export interface Props {
    topic: Topic
    publicationKind?: 'article' | 'gallery'
    topicSource?: string
    section: TheNightlySection
    getAdTargeting: GetRouteAdTargeting
    classification?: ClassificationV4DTO
    location: H.Location<any>
    baseUrl: string
    toggles: TogglesReduxState
}

const MAIN_CONTENT_ID = 'main-content'

export const getTopicPageRoute2026 = ({
    topic,
    publicationKind,
    topicSource,
    section,
    getAdTargeting,
    classification,
    location,
    baseUrl,
    toggles,
}: Props): PageType<TheNightlySection> => {
    if (section === 'default') {
        throw new Error("'default' section does not exist.")
    }

    const featureState = toggles && toFeatureState(toggles)

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'home',
        section,
        topic,
    )

    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const query = queryString.parse(location.search)
    const page = parsePageParam(query.page)
    const title =
        page > 1
            ? `${topic.seoTitle || topic.title} | Page ${page}`
            : topic.seoTitle || topic.title
    const topicUrl = `${baseUrl}${location.pathname}`
    const canonicalUrl =
        page > 1 ? getPaginatedCanonicalUrl(topicUrl, page) : topicUrl

    const getBodyComposition = topic.parent
        ? getSubTopicComposition
        : getParentTopicComposition

    return {
        kind: 'page',
        heading: topic.title,
        hideHeading: true,
        pageType: 'topic',
        includeFromSectionInMetaDescription: !topic.seoDescription,
        pageMeta: {
            title,
            description:
                topic.seoDescription || `The latest in ${topic.seoTitle}`,
            meta: [
                {
                    property: 'og:description',
                    content:
                        topic.seoDescription ||
                        `The latest in ${topic.seoTitle}`,
                },
                {
                    property: 'og:title',
                    content: `${topic.seoTitle || topic.title} | The Nightly`,
                },
                {
                    property: 'twitter:description',
                    content:
                        topic.seoDescription ||
                        `The latest in ${topic.seoTitle}`,
                },
            ],
            link: [{ rel: 'canonical', href: canonicalUrl }],
        },
        adTargeting,
        section,
        compositions: [
            wrappingContexts(
                [
                    layout.nestedComposition({
                        type: 'thor',
                        props: {
                            horizontalGutters: false,
                            verticalGutters: false,
                            containerWidth:
                                metrics.thenightly.siteMetrics.mainContentWidth,
                        },
                        contentAreas: {
                            main: [
                                layout.component({
                                    type: 'section-header',
                                    props: {
                                        heading: topic.title,
                                        showReadMore: false,
                                        isStandalonePage: true,
                                    },
                                }),
                            ],
                        },
                    }),

                    // horizontal nav menu
                    layout.nestedComposition({
                        type: 'tag-link-navigation',
                        props: {
                            sectionNavItem: sectionNavItems[section],
                            forceHorizontalLayout: true,
                        },
                        contentAreas: {
                            trailingContent: [],
                        },
                    }),

                    ...getBodyComposition({
                        section,
                        topic,
                        adTargeting,
                        contextId: MAIN_CONTENT_ID,
                        featureState,
                    }),

                    // footer
                    layout.component({
                        type: 'nightly-button',
                        props: {
                            variant: 'default',
                            text: 'BACK TO TOP',
                            color: 'primary',
                            fill: 'text',
                            layout: {
                                alignSelf: 'center',
                            },
                            action: {
                                type: 'button',
                                onClick: () => window.scrollTo({ top: 0 }),
                            },
                            icon: {
                                iconPosition: 'right',
                                IconElement: <IconChevronUpTN />,
                            },
                        },
                    }),
                    layout.component({
                        type: 'ad-unit',
                        props: {
                            adType: 'inline',
                            noticePosition: 'above-center',
                            slot: {
                                id: 'below-page-content',
                                size: 'billboardAbove1280leaderboardAbove768MrecBelow',
                            },
                        },
                    }),
                ],
                section,
                getDataDefinitionArgs({
                    topic,
                    publicationKind,
                    topicSource,
                    classification,
                    page,
                }),
                MAIN_CONTENT_ID,
            ),
        ],
    }
}

function getDataDefinitionArgs({
    topic,
    publicationKind,
    topicSource,
    classification,
    page,
}: {
    topic: Topic
    publicationKind?: 'article' | 'gallery'
    topicSource?: string
    classification?: ClassificationV4DTO
    page: number
}): ContentDataTypes {
    // Video page gets 5 articles from curation instead
    if (topic.id === 'video') {
        return {
            type: 'curation',
            name: 'thenightly-video',
            offset: 0,
            pageSize: 5,
        }
    }

    // Regular topic page gets articles from topic listing
    return {
        type: 'listing',
        topics: [topic.id],
        publicationKind,
        source: topicSource,
        classification,
        paging: {
            page,
            pageSize: topic.parent ? 18 : 4,
            pageOffset: 0,
        },
    }
}
